// ActivityWizard state — useReducer + Context (Q3-låsning).
//
// Single source of truth for wizard data (Lesson 38). Extension-points
// commented for delspår 3b-3g (each step adds its own formData-fields).
// In 3a, formData is a typed-skeleton with all 6 steg defined but empty —
// future delspår fill in real fields.

import { createContext, useContext, useReducer, type ReactNode, type Dispatch } from "react";

// Per spec sektion 11 — 6 steg-shape. Each delspår 3b-3g fills its step's
// fields. In 3a, all are nullable placeholders.
export interface ActivityWizardFormData {
  // Steg 1 — sport + typ + synlighet (delspår 3b)
  sport_type?: string | null;     // DB-kolumn activities.sport_type (text, default 'padel')
  activityTypeId?: string | null;
  visibility?: string | null;     // DB-kolumn activities.visibility ('public'|'private', default 'public')
  // Steg 2 — datum + längd + tid + bana (delspår 3c)
  date?: string | null;            // ISO yyyy-mm-dd (Stockholm-local)
  durationMinutes?: number | null; // längd (flyttad från 3d); endTime härleds
  startTime?: string | null;       // HH:mm (Stockholm-local)
  endTime?: string | null;         // härleds: startTime + durationMinutes
  // Steg 3 — spelare + nivå + poäng + bana-läge (delspår 3d; bana flyttad från 3c per Lesson 49)
  spotsTotal?: number | null;      // max spelarantal → activities.spots_total
  minParticipants?: number | null;
  minLevel?: number | null;        // 1-10
  maxLevel?: number | null;        // 1-10
  targetPoints?: number | null;    // → tournaments.points_per_match (endast tournament-typer, sätts vid 3g)
  // Runtime-invariant: courtMode/courtIds är bara meningsfulla när wantsCourt===true
  // (annars ignoreras de vid 3g). wantsCourt=false = väg A (ingen bana).
  wantsCourt?: boolean;            // "Vill du boka bana?" (default true)
  courtMode?: "auto" | "specific"; // default 'auto' när wantsCourt=true
  courtIds?: string[];             // endast när courtMode='specific'; 3g härleder multi för 'auto'
  // Steg 4 — inbjudningar (delspår 3e)
  invitedUserIds?: string[];
  invitedEmails?: string[];
  invitedPhones?: string[];
  // Steg 5 — pris (delspår 3f)
  organizerFeeMinor?: number | null;
  paymentMode?: string | null;        // 'system' | 'external' | 'free' (default 'system')
  applyMemberDiscount?: boolean;       // medlemsrabatt-toggle; UI-default true, skickas ALLTID explicit till v2
  displayPriceMinor?: number | null;   // endast external — informativt pris arrangören tar utanför appen (öre)
  // 2e-core: per-aktivitet cancellation-fönster (h). null → create_activity_v2 löser org ?? 24.
  // 0 = giltig policy ("alla köp slutgiltiga", ingen gratis avbokning). Frozen vid CREATE.
  cancellationHours?: number | null;
  // Steg 6 — bekräftelse + extras (delspår 3g)
  activityTitle?: string | null;
  description?: string | null;
  includes?: string[];
}

export interface ActivityWizardState {
  currentStep: number;        // 1-6 (per spec sektion 11)
  formData: ActivityWizardFormData;
  /**
   * Set true once the user has interacted with any step beyond initial mount.
   * Used by Q26 abandon-dialog to skip dialog if nothing entered.
   */
  isDirty: boolean;
}

export type ActivityWizardAction =
  | { type: "SET_STEP"; payload: number }
  | { type: "NEXT_STEP" }
  | { type: "PREV_STEP" }
  | { type: "UPDATE_FORM"; payload: Partial<ActivityWizardFormData> }
  | { type: "SET_SPORT"; payload: string }
  | { type: "LOAD_DRAFT"; payload: { currentStep: number; formData: ActivityWizardFormData } }
  | { type: "RESET" };

export const TOTAL_STEPS = 6;

export const initialActivityWizardState: ActivityWizardState = {
  currentStep: 1,
  formData: {},
  isDirty: false,
};

export function activityWizardReducer(
  state: ActivityWizardState,
  action: ActivityWizardAction,
): ActivityWizardState {
  switch (action.type) {
    case "SET_STEP":
      return {
        ...state,
        currentStep: Math.max(1, Math.min(TOTAL_STEPS, action.payload)),
      };
    case "NEXT_STEP":
      // Advancing past a step counts as "pågående data" per spec Q26
      // ("Q26 gäller även om formData bara är 'step 2 visited'"). Mark dirty
      // so abandon-dialog (Q26) + localStorage-persistence (Q34) trigger even
      // when 3a-placeholder steps have no form-inputs to fire UPDATE_FORM.
      return {
        ...state,
        currentStep: Math.min(TOTAL_STEPS, state.currentStep + 1),
        isDirty: true,
      };
    case "PREV_STEP":
      return {
        ...state,
        currentStep: Math.max(1, state.currentStep - 1),
      };
    case "UPDATE_FORM":
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
        isDirty: true,
      };
    case "SET_SPORT":
      // Implicit, data-derived sport for the club (single-sport club, or default
      // pre-select for a multi-sport club). Deliberately does NOT set isDirty —
      // this is system-init, not user input, so it must not trigger the Q26
      // abandon-dialog / Q34 draft-persist / beforeunload guard (phantom-dirty,
      // cf DEFECT D4 + Lesson D2). User sport-picks use UPDATE_FORM instead.
      return {
        ...state,
        formData: { ...state.formData, sport_type: action.payload },
      };
    case "LOAD_DRAFT":
      return {
        currentStep: action.payload.currentStep,
        formData: action.payload.formData,
        isDirty: true,
      };
    case "RESET":
      return initialActivityWizardState;
    default:
      return state;
  }
}

interface ActivityWizardContextValue {
  state: ActivityWizardState;
  dispatch: Dispatch<ActivityWizardAction>;
}

const ActivityWizardContext = createContext<ActivityWizardContextValue | null>(null);

export function ActivityWizardProvider({
  children,
  initialState,
}: {
  children: ReactNode;
  initialState?: ActivityWizardState;
}) {
  const [state, dispatch] = useReducer(
    activityWizardReducer,
    initialState ?? initialActivityWizardState,
  );
  return (
    <ActivityWizardContext.Provider value={{ state, dispatch }}>
      {children}
    </ActivityWizardContext.Provider>
  );
}

export function useActivityWizard(): ActivityWizardContextValue {
  const ctx = useContext(ActivityWizardContext);
  if (!ctx) {
    throw new Error("useActivityWizard must be used within <ActivityWizardProvider>");
  }
  return ctx;
}
